import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {EInputType, IEditConfig, IInputConfig} from "../../../interface/table.config";
import {NgClass, NgForOf, NgIf, TitleCasePipe} from "@angular/common";
import {MatError, MatFormField, MatFormFieldModule, MatLabel} from "@angular/material/form-field";
import {MatInput, MatInputModule} from "@angular/material/input";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatDatepicker} from "@angular/material/datepicker";
import {MatCheckbox, MatCheckboxChange} from "@angular/material/checkbox";
import {MatIcon} from "@angular/material/icon";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../environments/environment";
import {EditorModule} from "@tinymce/tinymce-angular";
import {EventObj} from "@tinymce/tinymce-angular/editor/Events";

const errorTranslate: Record<string, string> = {
  'required': 'Required',
  'pattern': 'Wrong Format',
  'maxlength': 'Too Long',
  'email': 'Should Be An Email',
};

@Component({
  selector: 'app-base-edit',
  standalone: true,
  imports: [
    NgForOf,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatLabel,
    NgIf,
    MatButton,
    MatError,
    MatSelect,
    MatOption,
    MatDatepicker,
    MatCheckbox,
    TitleCasePipe,
    FormsModule,
    MatIconButton,
    MatIcon,
    MatFormFieldModule,
    MatInputModule,
    NgClass,
    EditorModule,
  ],
  templateUrl: './base-edit.component.html',
  styleUrl: './base-edit.component.scss'
})
export class BaseEditComponent implements OnChanges {
  @Input() editConfig: IEditConfig;
  @Input() item: any;
  @Input() fieldsOnly: boolean = false;

  @Output() action = new EventEmitter<{name: string, data?: any}>();
  @Output() updateForm = new EventEmitter<{form?: FormGroup}>();

  formGroup: FormGroup;

  marksTarget: string = '';
  marksTargets = [
    'inst', 'fb', 'x', 'don', 'goal', 'raised', 'pay'
  ];

  data: Record<string, Record<string, boolean>> = {};

  modules: { key: string, label?: string }[] = [];

  visible: string[] = [];

  constructor(protected formBuilder: FormBuilder,
              protected cdr: ChangeDetectorRef,
              protected httpClient: HttpClient) {

  }

  toggle(field: string) {
    if (this.visible.includes(field)) {
      this.visible = this.visible.filter((value: string) => value !== field);
    } else {
      this.visible.push(field);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const controls: Record<string, any> = {};

    this.data = {};
    for (const group of this.editConfig.groups) {

      for (const input of group.configs) {
        let val = this.item[input.field];

        if (input.type === EInputType.SELECT) {
          val = val?.toString();
        }


        if (input.type === EInputType.ROLE_ACCESS) {
          let settings: {access?: Record<string, any>} = {};
          try {
            settings = val ? JSON.parse(val) : {};
          } catch (err) {
            console.error(err);
          }

          const access = settings?.access || {};

          this.data[input.field] = {};
          for (const moduleKey of Object.keys(access)) {
            this.data[input.field][moduleKey] = !!access[moduleKey];
          }

          controls[input.field] =[JSON.stringify(settings), input.validators];
        } if (input.type === EInputType.FILE || input.type === EInputType.FILE_WITH_MARKS) {

          const path = input.value ? input.value(this.item) : val;

          controls[input.field] = [path || 'no file selected', input.validators];
        } else {
          controls[input.field] =[input.value ? input.value(this.item) : val, input.validators];
        }


        if (input.type === EInputType.ROLE_ACCESS && !this.modules.length) {

          this.httpClient.get(environment.apiUrl + '/me/modules', {
            headers: {
              'Authorization': 'Bearer ' + localStorage.getItem('jwtToken'),
            }
          }).toPromise().then((data: any) => {
            this.modules = data.roles.sort((a: string,b: string) => {
              return a.localeCompare(b);
            }).map((role: string) => ({
              key: role,
              label: role.replace('\-', ' ')
            }));

            this.cdr.detectChanges();
          }).catch(console.error);

        }
      }
    }

    this.formGroup = this.formBuilder.group(controls);

    this.updateForm.next({form: this.formGroup});


    // this.formGroup.controls['createdAt'].patchValue('2022-02-02T00:00')
  }

  onChangeBoolean(field: string, event: MatCheckboxChange) {
    this.formGroup.controls[field].patchValue(event.checked);
  }

  onChange(config: IInputConfig, event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target.files?.length) {
      return;
    }

    const file  = target.files[0];

    const formData = new FormData();
    formData.set('file', file);

    this.formGroup.controls[config.field].patchValue('loading...');

    this.httpClient.post(environment.apiUrl + '/me/upload-file', formData, {
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem('jwtToken'),
      }
    }).toPromise().then((data: any) => {
      const prefix = config.cdnWithSuffix ? environment.apiUrl + '/files/' : '';

      this.formGroup.controls[config.field].patchValue(prefix + data.path.split('\\').slice(-1)[0]);
    }).catch((err) => {

      this.formGroup.controls[config.field].patchValue(err.message);
    });


  }

  onRoleSelect(field: string, key = '') {
    if (key === 'read_all' || key === 'write_all') {
      for (const dataKey of Object.keys(this.data[field])) {
        if (key.startsWith('read') === dataKey.startsWith('read') && dataKey !== key) {
          this.data[field][dataKey] = false;
        }
      }
    } else {
      if (key.startsWith('read')) {
        this.data[field]['read_all'] = false;
      } else {
        this.data[field]['write_all'] = false;
      }
    }

    this.formGroup.controls[field].patchValue(JSON.stringify({access: this.data[field]}));
  }
  onSubmit() {
    this.action.next({
      name: 'submit',
      data: this.formGroup.value,
    });
  }

  onBack() {
    this.action.next({
      name: 'back',
    });
  }

  hasError(field: string) {
    return !this.formGroup.controls[field].valid;
  }

  pickMarksTarget(tool: string) {
    this.marksTarget = this.marksTarget === tool ? '' : tool;
  }

  getError(field: string) {
    const error = this.formGroup.controls[field].errors ? Object.keys(this.formGroup.controls[field].errors || {})[0] : 'Error';
    return errorTranslate[error] || error;
  }

  get cdnUrl() {
    return environment.apiUrl + '/files/';
  }

  applyTarget(eve: MouseEvent) {
    if (this.marksTarget) {


      const ox = eve.offsetX;
      const oy = eve.offsetY;
      const fx = (eve.target as HTMLImageElement).width;
      const fy = (eve.target as HTMLImageElement).height;

      const inpX = document.querySelector(`[name=${this.marksTarget}X]`);
      const inpY = document.querySelector(`[name=${this.marksTarget}Y]`);

      if (inpX) (inpX as HTMLInputElement).value = (ox/fx).toFixed(4);
      if (inpY) (inpY as HTMLInputElement).value = (oy/fy).toFixed(4);

      this.formGroup.controls[this.marksTarget + 'X'].patchValue((ox/fx).toFixed(4));
      this.formGroup.controls[this.marksTarget + 'Y'].patchValue((oy/fy).toFixed(4));

      this.marksTarget = '';
    }
  }

  protected readonly EInputType = EInputType;
  protected readonly environment = environment;
}
